// Story 7.18 (ADO-1387) — Catalogue barcode-resolution read client.
//
// Thin typed wrapper over the Bearer-attaching `apiFetch` transport for the
// dedicated barcode-resolution route (`GET /catalogue/barcode/:code`). Mirrors
// `search.client.ts` / `browse.client.ts`: the SINGLE consumer seam until the
// `openapi-typescript`-generated client lands (Story 6.1 D-1). Exercised against
// msw in tests.
//
// This route is DELIBERATELY separate from `searchProducts` (DD-1 / Deviation D-4):
// `GET /catalogue/search` collapses the invalid / not-found / discontinued states
// into one empty result, so it cannot drive the AC5 disambiguation picker or the
// distinct AC7 messages. This client returns the server's discriminated `kind`
// verbatim; the UI chooses its copy off `kind`.

import { API_PREFIX, apiFetch } from './shoplink-api';
import type { SearchResultProduct } from './search.client';
import { withEntry } from './catalogue-entry';

/**
 * The four outcomes of resolving a scanned/typed barcode. Lock-step with
 * `BarcodeResolutionDto` in the backend `search-and-discovery` contracts.
 *
 * - `unreadable`: not a usable GTIN / failed the mod-10 check digit (AC3); the
 *   server never hit the DB. → the "not a valid barcode" message (AC7). `reason` is
 *   a diagnostic; the displayed copy is chosen off `kind`.
 * - `matched`: one OR MORE products carry the code in the selected entry's range; the
 *   caller disambiguates (AC5) or confirms the single match (AC6). Never a
 *   silently-picked winner. A product off sale in the range comes back only when it has
 *   a substitute, marked `notAvailable` as a search row is (Enabler E7-C, ADO-2274,
 *   D-6): it cannot be added, and the scanner shows search's not-available card.
 * - `discontinued`: the code is known but every product carrying it is retired, or is
 *   off sale in the range with no substitute; a DISTINCT message.
 * - `unknown`: a valid GTIN no product the caller can see in the range carries.
 *   → the "not in catalogue" message (AC7).
 */
export type BarcodeResolution =
  | { readonly kind: 'unreadable'; readonly reason: string }
  | {
      readonly kind: 'matched';
      readonly barcode: string;
      readonly products: readonly SearchResultProduct[];
    }
  | { readonly kind: 'discontinued'; readonly barcode: string }
  | { readonly kind: 'unknown'; readonly barcode: string };

export const BARCODE_PATH = `${API_PREFIX}/catalogue/barcode`;

/**
 * A 2xx body is only usable once it carries a KNOWN `kind` plus that kind's required
 * fields. Mirrors `basket.client`'s `parseBasket`/`isBasketLine`: without this, the
 * `as BarcodeResolution` cast below is a lie the compiler can't catch, and an
 * off-contract body (client↔server version skew, a renamed/extra `kind`, an
 * intermediary) would flow into `mapResolution` — whose `switch` matches nothing,
 * returns `undefined`, and white-screens the whole modal instead of degrading to the
 * retryable `resolveError` state. An unrecognised shape is a contract violation, so it
 * THROWS (→ caller's `resolveError`), never a silent value-typed `undefined`.
 */
function isBarcodeResolution(body: unknown): body is BarcodeResolution {
  if (typeof body !== 'object' || body === null) return false;
  const record = body as Record<string, unknown>;
  switch (record.kind) {
    case 'unreadable':
      return typeof record.reason === 'string';
    case 'matched':
      return typeof record.barcode === 'string' && Array.isArray(record.products);
    case 'discontinued':
    case 'unknown':
      return typeof record.barcode === 'string';
    default:
      return false;
  }
}

/**
 * Resolve one raw barcode `code` (AC4/AC5/AC7). Every RESOLUTION outcome — including
 * "not valid", "not in catalogue" and "discontinued" — is a valid 2xx body carrying
 * a `kind`, never an error: the empty-vs-failed distinction the UI depends on
 * (`ResolveBarcodeService` returns these as values, not throws). Only a transport or
 * non-2xx failure throws, so the caller can surface a retryable error separately from
 * a legitimate "no match" (AC7 / Dev Notes "Empty vs failed").
 *
 * The `code` is sent as a STRING with its significant leading zeros intact (AC3) —
 * `encodeURIComponent` keeps it verbatim in the path; it is never coerced to a number.
 * An optional `AbortSignal` lets a superseded/unmounted scan abort the read (mirrors
 * `searchProducts`); an aborted fetch rejects with a DOMException named `AbortError`,
 * which the caller distinguishes from a genuine transport failure.
 *
 * Enabler E7-C (ADO-2274), D-1: `entry` is the selected ordering entry; a barcode
 * resolves to that entry's range's listings.
 */
export async function resolveBarcode(
  entry: string,
  code: string,
  signal?: AbortSignal,
): Promise<BarcodeResolution> {
  const res = await apiFetch(
    withEntry(`${BARCODE_PATH}/${encodeURIComponent(code)}`, entry),
    signal ? { signal } : {},
  );
  if (!res.ok) throw new Error(`Failed to resolve barcode (${res.status})`);
  const body: unknown = await res.json();
  if (!isBarcodeResolution(body)) {
    throw new Error('Barcode resolution response was not in the expected shape');
  }
  return body;
}
